"use client";

import { useEffect, useContext, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Checkbox,
  FormControlLabel,
  Tooltip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Card,
  CardContent,
  Divider,
  InputAdornment,
  alpha,
  Modal,
  Paper,
  Snackbar,
  Alert,
  useTheme,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { ErpContext } from "../../context/ErpContext.js";
import CrearEntidadClienteModalComponent from "./CrearEntidadClienteModalComponent.js";
import ModalDetalleProveedorCC from "./ModalDetalleProveedorCC.js";
import ModalDocumentosAdjuntos from "./ModalDocumentosAdjuntos.js";
import ModalMedioDePago from "../transaccionComponent/ModalMedioDePago.js";

import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PaymentsIcon from "@mui/icons-material/Payments";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import RefreshIcon from "@mui/icons-material/Refresh";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import BadgeIcon from "@mui/icons-material/Badge";
import DeleteIcon from "@mui/icons-material/Delete";
import InventoryIcon from "@mui/icons-material/Inventory";
import WarningIcon from "@mui/icons-material/Warning";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import jsPDF from "jspdf";
import "jspdf-autotable";
import { deliverPdf, reservePdfTarget } from "../../context/pdf_esqueletos/helpers/pdfDelivery.js";
import { drawPdfLogo } from "../../context/pdf_esqueletos/helpers/pdfLogo.js";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#5b5b5f" },
    secondary: { main: "#f4b10e" },
    success: { main: "#4CAF50" },
    error: { main: "#F44336" },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": { boxShadow: "0 4px 8px rgba(0,0,0,0.1)" },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        },
      },
    },
  },
});

const formatMoney = (value) => {
  const num = Number(value) || 0;
  return `$${num.toLocaleString("es-AR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

const moneyColor = (value) => {
  const num = Number(value) || 0;
  if (num > 0) return "#2e7d32";
  if (num < 0) return "#c62828";
  return "#555";
};

const moneyBg = (value) => {
  const num = Number(value) || 0;
  if (num > 0) return "#e8f5e9";
  if (num < 0) return "#ffebee";
  return "transparent";
};

const GestionProveedoresComponent = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { tenant } = useParams();
  const {
    getProveedoresConCuentaCorriente,
    deleteEntidadProveedor,
    getCountServiciosByProveedor,
    postTransaccion,
    postCrearPago,
    postTransaccionPago,
    getParametrosGlobales,
    getEntidadByID,
    user,
    tieneAccesoFuncionalidad,
  } = useContext(ErpContext);

  // Permisos granulares (COMPRAS4 - Proveedores)
  const mostrarColumnaMonto = tieneAccesoFuncionalidad(user, "COMPRAS4", "COMPRAS4_columnaMonto");
  const mostrarColumnaConsumido = tieneAccesoFuncionalidad(user, "COMPRAS4", "COMPRAS4_columnaConsumido");
  const mostrarColumnaSaldo = tieneAccesoFuncionalidad(user, "COMPRAS4", "COMPRAS4_columnaSaldo");
  const mostrarColumnaPagos = tieneAccesoFuncionalidad(user, "COMPRAS4", "COMPRAS4_columnaPagos");
  const mostrarColumnasAcciones = tieneAccesoFuncionalidad(user, "COMPRAS4", "COMPRAS4_columnasAcciones");
  const mostrarBtnNuevoProveedor = tieneAccesoFuncionalidad(user, "COMPRAS4", "COMPRAS4_btnNuevoProveedor");
  const mostrarBtnExportarPdf = tieneAccesoFuncionalidad(user, "COMPRAS4", "COMPRAS4_btnExportarPdf");
  const mostrarFooterMontoTotal = tieneAccesoFuncionalidad(user, "COMPRAS4", "COMPRAS4_footerMontoTotal");
  const mostrarFooterConsumidoTotal = tieneAccesoFuncionalidad(user, "COMPRAS4", "COMPRAS4_footerConsumidoTotal");
  const mostrarFooterSaldoTotal = tieneAccesoFuncionalidad(user, "COMPRAS4", "COMPRAS4_footerSaldoTotal");

  const [proveedores, setProveedores] = useState([]);
  const [filteredProveedores, setFilteredProveedores] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filtros
  const [searchNombre, setSearchNombre] = useState("");
  const [searchDni, setSearchDni] = useState("");
  const [filtroEsVendedor, setFiltroEsVendedor] = useState(false);
  const [filtroNoEsVendedor, setFiltroNoEsVendedor] = useState(false);

  // Ordenamiento
  const [orderBy, setOrderBy] = useState("descripcion");
  const [order, setOrder] = useState("asc");

  // Modal crear/editar
  const [modalOpen, setModalOpen] = useState(false);
  const [entidadSeleccionada, setEntidadSeleccionada] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const [alerta, setAlerta] = useState(null);

  // Modal detalle CC
  const [detalleModalOpen, setDetalleModalOpen] = useState(false);
  const [detalleIdEntidad, setDetalleIdEntidad] = useState(null);

  // Modal de pago (adelanto)
  const [pagoModalOpen, setPagoModalOpen] = useState(false);
  const [proveedorParaPago, setProveedorParaPago] = useState(null);

  // Modal documentos adjuntos
  const [openModalDocumentos, setOpenModalDocumentos] = useState(false);
  const [adjuntosDocumentos, setAdjuntosDocumentos] = useState(null);
  const [loadingDocumentos, setLoadingDocumentos] = useState(false);

  // Modal confirmar eliminar proveedor
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, proveedor: null, serviciosCount: 0, servicios: [] });
  const [loadingDelete, setLoadingDelete] = useState(false);

  const fetchProveedores = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getProveedoresConCuentaCorriente();
      setProveedores(data);
      setFilteredProveedores(data);
    } catch (error) {
      console.error("Error al cargar proveedores:", error);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // getProveedoresConCuentaCorriente no es estable (no tiene useCallback en contexto); omitirla evita re-fetches en cada render del contexto

  useEffect(() => {
    fetchProveedores();
  }, [fetchProveedores]);

  // Aplicar filtros
  useEffect(() => {
    let filtered = [...proveedores];

    if (searchNombre.trim()) {
      const q = searchNombre.trim().toLowerCase();
      filtered = filtered.filter((p) => {
        const nombre = `${p.descripcion || ""} ${p.apellido || ""}`.toLowerCase();
        return nombre.includes(q);
      });
    }

    if (searchDni.trim()) {
      const q = searchDni.trim().toLowerCase();
      filtered = filtered.filter(
        (p) => p.dniCuitCuil && p.dniCuitCuil.toLowerCase().includes(q)
      );
    }

    if (filtroEsVendedor && !filtroNoEsVendedor) {
      filtered = filtered.filter((p) => p.esVendedor === true);
    } else if (!filtroEsVendedor && filtroNoEsVendedor) {
      filtered = filtered.filter((p) => !p.esVendedor);
    }

    // Ordenamiento
    filtered = [...filtered].sort((a, b) => {
      let valA, valB;
      if (orderBy === "descripcion") {
        valA = `${a.descripcion || ""} ${a.apellido || ""}`.toLowerCase();
        valB = `${b.descripcion || ""} ${b.apellido || ""}`.toLowerCase();
      } else if (orderBy === "dniCuitCuil") {
        valA = a.dniCuitCuil || "";
        valB = b.dniCuitCuil || "";
      } else {
        valA = Number(a[orderBy]) || 0;
        valB = Number(b[orderBy]) || 0;
      }
      if (valA < valB) return order === "asc" ? -1 : 1;
      if (valA > valB) return order === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredProveedores(filtered);
  }, [proveedores, searchNombre, searchDni, filtroEsVendedor, filtroNoEsVendedor, orderBy, order]);

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(col);
      setOrder("asc");
    }
  };

  const handleCrear = () => {
    setEntidadSeleccionada(null);
    setIsEditing(false);
    setModalOpen(true);
  };

  const handleEditar = async (proveedor) => {
    try {
      const entidadCompleta = await getEntidadByID(proveedor.id);
      setEntidadSeleccionada({
        ...proveedor,
        ...entidadCompleta,
        idTipoEntidad: 2,
      });
    } catch (error) {
      console.error("Error al cargar entidad completa:", error);
      setEntidadSeleccionada({
        ...proveedor,
        idTipoEntidad: 2,
      });
    }
    setIsEditing(true);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEntidadSeleccionada(null);
    setIsEditing(false);
    fetchProveedores();
  };

  const handleVerDetalle = (proveedor) => {
    setDetalleIdEntidad(proveedor.id);
    setDetalleModalOpen(true);
  };

  const handleDetalleClose = () => {
    setDetalleModalOpen(false);
    setDetalleIdEntidad(null);
  };

  const handleAbrirPago = (proveedor) => {
    setProveedorParaPago(proveedor);
    setPagoModalOpen(true);
  };

  const handleCerrarPago = () => {
    setPagoModalOpen(false);
    setProveedorParaPago(null);
  };

  const handleEliminar = async (proveedor) => {
    setLoadingDelete(true);
    try {
      const { count, servicios } = await getCountServiciosByProveedor(proveedor.id);
      setDeleteConfirm({ open: true, proveedor, serviciosCount: count, servicios });
    } catch (err) {
      setAlerta({ tipo: "error", mensaje: "No se pudo verificar servicios del proveedor. Intente nuevamente." });
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleAbrirDocumentos = async (proveedor) => {
    setOpenModalDocumentos(true);
    setLoadingDocumentos(true);
    try {
      const entidad = await getEntidadByID(proveedor.id);
      setAdjuntosDocumentos(entidad?.adjuntos || null);
    } catch (error) {
      console.error("Error al cargar documentos adjuntos:", error);
      setAdjuntosDocumentos(null);
    } finally {
      setLoadingDocumentos(false);
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteConfirm({ open: false, proveedor: null, serviciosCount: 0, servicios: [] });
  };

  const handleConfirmarEliminar = async () => {
    if (!deleteConfirm.proveedor) return;
    setLoadingDelete(true);
    try {
      await deleteEntidadProveedor(deleteConfirm.proveedor.id);
      handleCloseDeleteModal();
      fetchProveedores();
    } catch (err) {
      const msg = err?.response?.data?.error || "Error al eliminar proveedor";
      setAlerta({ tipo: "error", mensaje: msg });
      handleCloseDeleteModal();
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleIrAServicios = () => {
    const tenantPath = tenant || "default";
    navigate(`/${tenantPath}/verServicios`, {
      state: {
        proveedorId: deleteConfirm.proveedor?.id,
        proveedorDescripcion: deleteConfirm.proveedor?.descripcion,
      },
    });
    handleCloseDeleteModal();
  };

  const puedeEliminar = (p) => {
    const monto = Number(p.montoAcumulado) || 0;
    const consumido = Number(p.cobradoAcumulado) || 0;
    const saldo = Number(p.saldoDinamico) || 0;
    return monto === 0 && consumido === 0 && saldo === 0;
  };

  const handleConfirmarPago = async (mediosDePago, montoTotalMediosPago, _transaccionSeleccionada, descripcion, idUbicacionSeleccionada, idCategorizacion) => {
    try {
      // Detectar si algún medio de pago es Cuenta Corriente (idTipoMedioDePago = 5)
      const esPagoConCC = mediosDePago.some(mp => mp.tipoMedioDePago === 5);
      
      const idTipoTransaccion = esPagoConCC ? 8 : 5; // 8 = Cuenta Corriente, 5 = Egreso
      const operacionCC = esPagoConCC ? 'operacionCC' : 'adelantoCC'; // operacionCC resta, adelantoCC suma

      const transaccionResponse = await postTransaccion(
        montoTotalMediosPago,           // monto
        idTipoTransaccion,              // 8 si es CC, 5 si es adelanto
        proveedorParaPago.id,           // idEntidad
        idCategorizacion ?? null,       // subcategoría (obligatoria solo si no es CC)
        descripcion || null,            // sin descripción default
        user?.id,
        idUbicacionSeleccionada || 1,   // idUbicacion seleccionada desde el modal
        1,                              // idNegocio
        0,                              // descuento
        0,                              // porcentaje descuento
        null,                           // transaccionAsociada
        null,                           // fecha (usa hoy)
        true,                           // afectaCuentaCorriente
        null,                           // clientOS
        true,                           // viaja
        false,                          // liberado
        null,                           // cuotas
        operacionCC                     // 'operacionCC' resta, 'adelantoCC' suma
      );
      const idTransaccion = transaccionResponse?.data?.id;
      for (const it of mediosDePago) {
        const pagoCreado = await postCrearPago(1, 1, it.id, it.monto);
        await postTransaccionPago(idTransaccion, pagoCreado.data.id);
      }
    } catch (err) {
      console.error("Error al registrar pago al proveedor:", err);
    } finally {
      setPagoModalOpen(false);
      setProveedorParaPago(null);
      fetchProveedores();
    }
  };

  const handleFiltroEsVendedor = (val) => {
    if (val === "es") {
      setFiltroEsVendedor((prev) => {
        if (!prev) setFiltroNoEsVendedor(false);
        return !prev;
      });
    } else {
      setFiltroNoEsVendedor((prev) => {
        if (!prev) setFiltroEsVendedor(false);
        return !prev;
      });
    }
  };

  const totalesFiltrados = filteredProveedores.reduce(
    (acc, p) => ({
      monto: acc.monto + (Number(p.montoAcumulado) || 0),
      consumido: acc.consumido + (Number(p.cobradoAcumulado) || 0),
      saldo: acc.saldo + (Number(p.saldoDinamico) || 0),
    }),
    { monto: 0, consumido: 0, saldo: 0 }
  );

  const exportarPDF = async () => {
    const deliveryContext = { targetWindow: reservePdfTarget() };
    let logoBase64 = null;
    try {
      const paramsLogo = await getParametrosGlobales(["logoNavbarTenant"]);
      logoBase64 = paramsLogo?.logoNavbarTenant || null;
    } catch (logoError) {
      console.error("Error al cargar logo para PDF de Gestión de Proveedores:", logoError);
    }

    const doc = new jsPDF({ orientation: "landscape" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;
    let headerY = 12;

    const { height: logoHeight } = drawPdfLogo({
      doc,
      logoBase64,
      x: margin,
      y: headerY,
      maxW: 28.2,
      maxH: 12.7,
    });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(33, 33, 33);
    doc.text("Gestión de Proveedores", pageWidth / 2, headerY + 14, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(117, 117, 117);
    doc.text(`Fecha de generación: ${new Date().toLocaleString("es-AR")}`, pageWidth - margin, headerY + 26, { align: "right" });

    const startY = Math.max(22, headerY + Math.max(logoHeight || 0, 24) + 14);

    const rows = filteredProveedores.map((p, i) => [
      i + 1,
      `${p.descripcion || ""} ${p.apellido || ""}`.trim(),
      p.dniCuitCuil || "",
      p.esVendedor ? "Sí" : "No",
      formatMoney(p.montoAcumulado),
      formatMoney(p.cobradoAcumulado),
      formatMoney(p.saldoDinamico),
      p.cantidadPagos || 0,
    ]);

    rows.push([
      "",
      "TOTALES",
      "",
      "",
      formatMoney(totalesFiltrados.monto),
      formatMoney(totalesFiltrados.consumido),
      formatMoney(totalesFiltrados.saldo),
      "",
    ]);

    doc.autoTable({
      head: [["#", "Nombre", "DNI/CUIT/CUIL", "Vendedor", "Monto", "Consumido", "Saldo", "# Pagos"]],
      body: rows,
      startY,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [91, 91, 95] },
      foot: [],
    });

    const fileName = "gestion-proveedores.pdf";
    doc.setProperties({
      title: fileName.replace(/\.pdf$/i, ""),
      subject: "Gestión de Proveedores",
      author: "LUTENTE TOURS",
      creator: "LUTENTE TOURS",
    });
    deliverPdf({
      doc,
      fileName,
      targetWindow: deliveryContext?.targetWindow || null,
    });
  };

  const sortableColumns = [
    { id: "descripcion", label: "Nombre", align: "left" },
    { id: "dniCuitCuil", label: "DNI/CUIT/CUIL", align: "left" },
  ];

  const sortableMoneyColumns = [
    ...(mostrarColumnaMonto ? [{ id: "montoAcumulado", label: "Monto", align: "right" }] : []),
    ...(mostrarColumnaConsumido ? [{ id: "cobradoAcumulado", label: "Consumido", align: "right" }] : []),
    ...(mostrarColumnaSaldo ? [{ id: "saldoDinamico", label: "Saldo", align: "right" }] : []),
  ];

  const thStyle = {
    backgroundColor: "#5b5b5f",
    color: "white",
    fontWeight: 600,
    fontSize: "0.78rem",
    padding: "10px 12px",
    whiteSpace: "nowrap",
    "& .MuiTableSortLabel-root": { color: "white" },
    "& .MuiTableSortLabel-root.Mui-active": { color: "white" },
    "& .MuiTableSortLabel-root:hover": { color: "#f4b10e" },
    "& .MuiTableSortLabel-icon": { color: "white !important" },
  };

  const leftCellIndentSx = {
    width: "100%",
    display: "flex",
    justifyContent: "flex-start",
    pl: 2,
  };

  const rightCellIndentSx = {
    width: "100%",
    display: "flex",
    justifyContent: "flex-end",
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          p: { xs: 1, sm: 2, md: 3 },
        }}
      >
        {/* ENCABEZADO - Card separado */}
        <Card
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: 2,
            overflow: "visible",
            flexShrink: 0,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            {/* Título con ícono */}
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <LocalShippingIcon
                sx={{
                  fontSize: 28,
                  color: "rgb(244, 147, 14)",
                  mr: 1.5,
                }}
              />
              <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
                Gestión de Proveedores
              </Typography>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Filtros y botones en línea */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: { xs: "wrap", sm: "nowrap" } }}>
                {/* Buscador Nombre */}
                <TextField
                  size="small"
                  label="Buscar por Nombre"
                  value={searchNombre}
                  onChange={(e) => setSearchNombre(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: { xs: "1 1 100%", sm: "1 1 auto" },
                    minWidth: { xs: "100%", sm: 180 },
                    "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  }}
                />

                {/* Buscador DNI */}
                <TextField
                  size="small"
                  label="Buscar por DNI/CUIT/CUIL"
                  value={searchDni}
                  onChange={(e) => setSearchDni(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BadgeIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: { xs: "100%", sm: 200 },
                    "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  }}
                />

                {/* Checkboxes Es Vendedor */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filtroEsVendedor}
                      onChange={() => handleFiltroEsVendedor("es")}
                      size="small"
                      sx={{
                        color: "#5b5b5f",
                        "&.Mui-checked": { color: "#5b5b5f" },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: "0.875rem", whiteSpace: "nowrap" }}>
                      Es Vendedor
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filtroNoEsVendedor}
                      onChange={() => handleFiltroEsVendedor("no")}
                      size="small"
                      sx={{
                        color: "#5b5b5f",
                        "&.Mui-checked": { color: "#5b5b5f" },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: "0.875rem", whiteSpace: "nowrap" }}>
                      NO Es Vendedor
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />

                {/* Botones a la derecha */}
                <Box sx={{ display: "flex", gap: 1, ml: { xs: 0, sm: "auto" }, flexShrink: 0, width: { xs: "100%", sm: "auto" }, justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<RefreshIcon />}
                    onClick={fetchProveedores}
                    disabled={loading}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      minWidth: 100,
                    }}
                  >
                    Actualizar
                  </Button>

                  {mostrarBtnExportarPdf && (
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={exportarPDF}
                    disabled={loading}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      minWidth: 100,
                    }}
                  >
                    PDF
                  </Button>
                  )}

                  {mostrarBtnNuevoProveedor && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCrear}
                    disabled={loading}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      boxShadow: 2,
                      minWidth: 140,
                      backgroundColor: "#5b5b5f",
                      "&:hover": {
                        backgroundColor: alpha("#5b5b5f", 0.8),
                      },
                    }}
                  >
                    Nuevo Proveedor
                  </Button>
                  )}
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* TABLA - Card separado */}
        <Card
          elevation={2}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
              <CircularProgress sx={{ color: "#5b5b5f" }} />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ flex: 1, overflow: "auto" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...thStyle, width: 40 }}>#</TableCell>

                      {sortableColumns.map((col) => (
                        <TableCell key={col.id} align={col.align} sx={thStyle}>
                          <TableSortLabel
                            active={orderBy === col.id}
                            direction={orderBy === col.id ? order : "asc"}
                            onClick={() => handleSort(col.id)}
                          >
                            {col.label}
                          </TableSortLabel>
                        </TableCell>
                      ))}

                      <TableCell align="center" sx={thStyle}>
                        Vendedor
                      </TableCell>

                      {sortableMoneyColumns.map((col) => (
                        <TableCell key={col.id} align={col.align} sx={thStyle}>
                          <TableSortLabel
                            active={orderBy === col.id}
                            direction={orderBy === col.id ? order : "asc"}
                            onClick={() => handleSort(col.id)}
                          >
                            {col.label}
                          </TableSortLabel>
                        </TableCell>
                      ))}

                      {mostrarColumnaPagos && (
                        <TableCell align="center" sx={thStyle}>
                          # Pagos
                        </TableCell>
                      )}
                      {mostrarColumnasAcciones && (
                        <TableCell align="center" sx={thStyle}>
                          Acciones
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {filteredProveedores.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3 + sortableMoneyColumns.length + (mostrarColumnaPagos ? 1 : 0) + (mostrarColumnasAcciones ? 1 : 0) + 1} align="center" sx={{ py: 6, color: "#999" }}>
                          No se encontraron proveedores
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredProveedores.map((p, idx) => (
                        <TableRow
                          key={p.id}
                          hover
                          sx={{
                            "&:nth-of-type(even)": { backgroundColor: "#fafafa" },
                            "&:hover": { backgroundColor: "#f0f0f2" },
                          }}
                        >
                          <TableCell sx={{ fontSize: "0.78rem", color: "#888", pl: 2 }}>
                            {idx + 1}
                          </TableCell>

                          <TableCell sx={{ fontSize: "0.85rem", fontWeight: 500 }}>
                            <Box sx={leftCellIndentSx}>
                              {`${p.descripcion || ""}${p.apellido ? " " + p.apellido : ""}`.trim()}
                            </Box>
                          </TableCell>

                          <TableCell sx={{ fontSize: "0.82rem", color: "#555" }}>
                            <Box sx={leftCellIndentSx}>
                              {p.dniCuitCuil || "-"}
                            </Box>
                          </TableCell>

                          <TableCell align="center">
                            <Checkbox
                              checked={p.esVendedor || false}
                              disabled
                              size="small"
                              sx={{
                                p: 0,
                                color: "#ccc",
                                "&.Mui-checked": { color: "#5b5b5f" },
                                "&.Mui-disabled": { color: p.esVendedor ? "#5b5b5f" : "#ccc" },
                              }}
                            />
                          </TableCell>

                          {/* Monto */}
                          {mostrarColumnaMonto && (
                          <TableCell align="right">
                            <Box sx={rightCellIndentSx}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  alignItems: "center",
                                  px: 1,
                                  py: 0.3,
                                  borderRadius: 1,
                                  backgroundColor: moneyBg(p.montoAcumulado),
                                  color: moneyColor(p.montoAcumulado),
                                  fontWeight: 600,
                                  fontSize: "0.82rem",
                                  width: 96,
                                  textAlign: "right",
                                }}
                              >
                                {formatMoney(p.montoAcumulado)}
                              </Box>
                            </Box>
                          </TableCell>
                          )}

                          {/* Consumido */}
                          {mostrarColumnaConsumido && (
                          <TableCell align="right">
                            <Box sx={rightCellIndentSx}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  alignItems: "center",
                                  px: 1,
                                  py: 0.3,
                                  borderRadius: 1,
                                  backgroundColor: moneyBg(p.cobradoAcumulado),
                                  color: moneyColor(p.cobradoAcumulado),
                                  fontWeight: 600,
                                  fontSize: "0.82rem",
                                  width: 96,
                                  textAlign: "right",
                                }}
                              >
                                {formatMoney(p.cobradoAcumulado)}
                              </Box>
                            </Box>
                          </TableCell>
                          )}

                          {/* Saldo */}
                          {mostrarColumnaSaldo && (
                          <TableCell align="right">
                            <Box sx={rightCellIndentSx}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  alignItems: "center",
                                  px: 1,
                                  py: 0.3,
                                  borderRadius: 1,
                                  backgroundColor: moneyBg(p.saldoDinamico),
                                  color: moneyColor(p.saldoDinamico),
                                  fontWeight: 700,
                                  fontSize: "0.82rem",
                                  width: 96,
                                  textAlign: "right",
                                }}
                              >
                                {formatMoney(p.saldoDinamico)}
                              </Box>
                            </Box>
                          </TableCell>
                          )}

                          {/* # Pagos */}
                          {mostrarColumnaPagos && (
                          <TableCell align="center" sx={{ fontSize: "0.82rem", color: "#555" }}>
                            {p.cantidadPagos || 0}
                          </TableCell>
                          )}

                          {/* Acciones */}
                          {mostrarColumnasAcciones && (
                          <TableCell align="center">
                            <Tooltip title="Ver detalle cuenta corriente">
                              <IconButton
                                size="small"
                                onClick={() => handleVerDetalle(p)}
                                sx={{
                                  color: "#5b5b5f",
                                  "&:hover": { backgroundColor: "#f0f0f2", color: "#1976d2" },
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Pagar / adelanto a proveedor">
                              <IconButton
                                size="small"
                                onClick={() => handleAbrirPago(p)}
                                sx={{
                                  color: "#5b5b5f",
                                  "&:hover": { backgroundColor: "#e8f5e9", color: "#2e7d32" },
                                }}
                              >
                                <PaymentsIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar proveedor">
                              <IconButton
                                size="small"
                                onClick={() => handleEditar(p)}
                                sx={{
                                  color: "#5b5b5f",
                                  "&:hover": { backgroundColor: "#f0f0f2", color: "#f4b10e" },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Documentos adjuntos">
                              <IconButton
                                size="small"
                                onClick={() => handleAbrirDocumentos(p)}
                                sx={{
                                  color: "#5b5b5f",
                                  "&:hover": { backgroundColor: "#f0f0f2", color: "#1976d2" },
                                }}
                              >
                                <PictureAsPdfIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={
                                puedeEliminar(p)
                                  ? "Eliminar proveedor"
                                  : "Solo se puede eliminar cuando Monto, Consumido y Saldo son 0"
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => puedeEliminar(p) && handleEliminar(p)}
                                  disabled={!puedeEliminar(p) || loadingDelete}
                                  sx={{
                                    color: puedeEliminar(p) ? "#5b5b5f" : "#ccc",
                                    "&:hover": puedeEliminar(p)
                                      ? { backgroundColor: "#ffebee", color: "#c62828" }
                                      : {},
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* PIE DE TABLA - TOTALES */}
              {filteredProveedores.length > 0 && (mostrarFooterMontoTotal || mostrarFooterConsumidoTotal || mostrarFooterSaldoTotal) && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    px: 3,
                    py: 1.5,
                    borderTop: "2px solid #e0e0e0",
                    backgroundColor: "#f5f5f7",
                    flexWrap: "wrap",
                    flexShrink: 0,
                  }}
                >
                  {mostrarFooterMontoTotal && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="caption" sx={{ color: "#888", fontWeight: 600 }}>
                      Monto Total
                    </Typography>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.4,
                        borderRadius: 1,
                        backgroundColor: moneyBg(totalesFiltrados.monto),
                        color: moneyColor(totalesFiltrados.monto),
                        fontWeight: 700,
                        fontSize: "0.85rem",
                      }}
                    >
                      {formatMoney(totalesFiltrados.monto)}
                    </Box>
                  </Box>
                  )}

                  {mostrarFooterConsumidoTotal && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="caption" sx={{ color: "#888", fontWeight: 600 }}>
                      Consumido Total
                    </Typography>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.4,
                        borderRadius: 1,
                        backgroundColor: moneyBg(totalesFiltrados.consumido),
                        color: moneyColor(totalesFiltrados.consumido),
                        fontWeight: 700,
                        fontSize: "0.85rem",
                      }}
                    >
                      {formatMoney(totalesFiltrados.consumido)}
                    </Box>
                  </Box>
                  )}

                  {mostrarFooterSaldoTotal && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="caption" sx={{ color: "#888", fontWeight: 600 }}>
                      Saldo Total
                    </Typography>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.4,
                        borderRadius: 1,
                        backgroundColor: moneyBg(totalesFiltrados.saldo),
                        color: moneyColor(totalesFiltrados.saldo),
                        fontWeight: 700,
                        fontSize: "0.88rem",
                      }}
                    >
                      {formatMoney(totalesFiltrados.saldo)}
                    </Box>
                  </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </Card>
      </Box>

      {/* MODAL CREAR / EDITAR */}
      <CrearEntidadClienteModalComponent
        open={modalOpen}
        handleClose={handleModalClose}
        entidad={entidadSeleccionada}
        isEditing={isEditing}
        handleAlerta={setAlerta}
        onClienteCreado={() => fetchProveedores()}
        tipoEntidad="proveedor"
      />

      {/* MODAL DETALLE CUENTA CORRIENTE */}
      <ModalDetalleProveedorCC
        open={detalleModalOpen}
        handleClose={handleDetalleClose}
        idEntidad={detalleIdEntidad}
      />

      {/* MODAL PAGAR / ADELANTO A PROVEEDOR */}
      {pagoModalOpen && proveedorParaPago && (
        <ModalMedioDePago
          open={pagoModalOpen}
          handleClose={handleCerrarPago}
          precioTotal={null}
          onConfirmarPago={handleConfirmarPago}
          transaccionSeleccionada={null}
          requiereSubcategoriaParaAdelanto
        />
      )}

      {/* MODAL CONFIRMAR ELIMINAR PROVEEDOR */}
      <Modal open={deleteConfirm.open} onClose={handleCloseDeleteModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            minWidth: deleteConfirm.serviciosCount > 0 ? 480 : 400,
            maxWidth: deleteConfirm.serviciosCount > 0 ? 560 : 440,
            maxHeight: "85vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {deleteConfirm.serviciosCount > 0 ? (
            <>
              {/* Header con advertencia */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 3,
                  py: 2,
                  bgcolor: alpha(theme.palette.warning.main, 0.12),
                  borderBottom: `2px solid ${theme.palette.warning.main}`,
                }}
              >
                <WarningIcon sx={{ fontSize: 28, color: theme.palette.warning.main }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.warning.dark }}>
                  No se puede eliminar el proveedor
                </Typography>
              </Box>

              <Box sx={{ p: 3, overflow: "auto", flex: 1 }}>
                {/* Proveedor que intenta eliminar */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: alpha(theme.palette.error.main, 0.04),
                    borderColor: alpha(theme.palette.error.main, 0.3),
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    Proveedor que intenta eliminar
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {deleteConfirm.proveedor
                      ? `${deleteConfirm.proveedor.descripcion || ""} ${deleteConfirm.proveedor.apellido || ""}`.trim()
                      : ""}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                    Tiene {deleteConfirm.serviciosCount} servicio(s) asociado(s). Debe eliminarlos primero para poder eliminar el proveedor.
                  </Typography>
                </Paper>

                {/* Lista de servicios */}
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: "text.primary" }}>
                  Servicios asociados ({deleteConfirm.serviciosCount})
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
                  {(deleteConfirm.servicios || []).map((s, idx) => (
                    <Paper
                      key={`${s.idItem}-${s.idUbicacion}-${idx}`}
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                        borderLeft: `4px solid ${theme.palette.primary.main}`,
                      }}
                    >
                      <Box sx={{ display: "grid", gap: 0.75 }}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Servicio</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {s.descripcion || s.codigo || `Servicio #${s.idItem}`}
                              {s.codigo && s.descripcion ? ` (${s.codigo})` : ""}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Destino</Typography>
                            <Typography variant="body2">{s.ubicacion || "N/A"}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Costo</Typography>
                            <Typography variant="body2">
                              {s.monto != null
                                ? `$${Number(s.monto).toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                                : "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>

                {/* Instrucción */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.info.main, 0.06),
                    borderColor: alpha(theme.palette.info.main, 0.25),
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Para eliminar este proveedor, vaya a <strong>Ver servicios</strong> y elimine los servicios asociados
                    primero.
                  </Typography>
                </Paper>
              </Box>

              {/* Footer con botones */}
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  borderTop: `1px solid ${theme.palette.divider}`,
                  bgcolor: alpha(theme.palette.background.paper, 0.5),
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                }}
              >
                <Button variant="outlined" onClick={handleCloseDeleteModal}>
                  Cancelar
                </Button>
                {/* <Button
                  variant="contained"
                  color="primary"
                  startIcon={<ArrowForwardIcon fontSize="small" />}
                  onClick={handleIrAServicios}
                  sx={{ textTransform: "none" }}
                >
                  Ver servicios
                </Button> */}
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ p: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                  <DeleteIcon color="error" />
                  Confirmar Eliminación
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  ¿Está seguro de eliminar al proveedor{" "}
                  <strong>
                    {deleteConfirm.proveedor
                      ? `${deleteConfirm.proveedor.descripcion || ""} ${deleteConfirm.proveedor.apellido || ""}`.trim()
                      : ""}
                  </strong>
                  ? Esta acción no se puede deshacer.
                </Typography>
                <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                  <Button variant="outlined" onClick={handleCloseDeleteModal} disabled={loadingDelete}>
                    Cancelar
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleConfirmarEliminar}
                    disabled={loadingDelete}
                    startIcon={loadingDelete ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
                  >
                    {loadingDelete ? "Eliminando..." : "Eliminar"}
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Modal>

      {/* Modal de documentos adjuntos */}
      <ModalDocumentosAdjuntos
        open={openModalDocumentos}
        handleClose={() => {
          setOpenModalDocumentos(false);
          setAdjuntosDocumentos(null);
        }}
        adjuntosJson={adjuntosDocumentos}
        loading={loadingDocumentos}
      />

      <Snackbar
        open={!!alerta}
        autoHideDuration={5000}
        onClose={() => setAlerta(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {alerta && (
          <Alert onClose={() => setAlerta(null)} severity={alerta.tipo || "error"}>
            {alerta.mensaje}
          </Alert>
        )}
      </Snackbar>
    </ThemeProvider>
  );
};

export default GestionProveedoresComponent;
